
import { useEffect, useState } from 'react';

interface PhoneAnimationProps {
  variant?: 'hero' | 'floating' | 'ringing' | 'pulse';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const PhoneAnimation = ({ variant = 'hero', size = 'lg', className = '' }: PhoneAnimationProps) => {
  const [isRinging, setIsRinging] = useState(false);

  useEffect(() => {
    if (variant === 'ringing' || variant === 'hero') {
      const interval = setInterval(() => {
        setIsRinging(true);
        setTimeout(() => setIsRinging(false), 1000);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [variant]);

  const sizeClasses = {
    sm: 'w-12 h-12',
    md: 'w-16 h-16',
    lg: 'w-24 h-24',
    xl: 'w-32 h-32',
  };

  const iconSizes = {
    sm: 'text-xl',
    md: 'text-2xl',
    lg: 'text-4xl',
    xl: 'text-5xl',
  };

  if (variant === 'hero') {
    return (
      <div className={`relative ${className}`}>
        {/* Outer pulse rings */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="absolute w-48 h-48 bg-teal/10 rounded-full animate-ping" style={{ animationDuration: '3s' }}></div>
          <div className="absolute w-40 h-40 bg-teal/20 rounded-full animate-ping" style={{ animationDuration: '2.5s', animationDelay: '0.5s' }}></div>
          <div className="absolute w-32 h-32 bg-teal/30 rounded-full animate-ping" style={{ animationDuration: '2s', animationDelay: '1s' }}></div>
        </div>
        
        {/* Phone icon container */}
        <div className={`relative ${sizeClasses[size]} bg-gradient-to-br from-teal to-teal/80 rounded-2xl flex items-center justify-center shadow-2xl shadow-teal/30 ${isRinging ? 'animate-bounce' : ''}`}>
          <i className={`ri-phone-line ${iconSizes[size]} text-white ${isRinging ? 'animate-pulse' : ''}`}></i>
          
          {/* Notification badge */}
          <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
            <span className="text-white text-xs font-bold">!</span>
          </div>
        </div>

        {/* Sound waves */}
        {isRinging && (
          <>
            <div className="absolute -right-4 top-1/2 -translate-y-1/2 flex flex-col gap-1">
              <div className="w-3 h-1 bg-teal rounded-full animate-pulse"></div>
              <div className="w-4 h-1 bg-teal/80 rounded-full animate-pulse" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-5 h-1 bg-teal/60 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
            </div>
            <div className="absolute -left-4 top-1/2 -translate-y-1/2 flex flex-col gap-1">
              <div className="w-3 h-1 bg-teal rounded-full animate-pulse"></div>
              <div className="w-4 h-1 bg-teal/80 rounded-full animate-pulse" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-5 h-1 bg-teal/60 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
            </div>
          </>
        )}
      </div>
    );
  }

  if (variant === 'floating') {
    return (
      <div className={`relative ${className}`}>
        <div className={`${sizeClasses[size]} bg-gradient-to-br from-teal to-teal/80 rounded-xl flex items-center justify-center shadow-lg animate-float`}>
          <i className={`ri-phone-line ${iconSizes[size]} text-white`}></i>
        </div>
        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          .animate-float {
            animation: float 3s ease-in-out infinite;
          }
        `}</style>
      </div>
    );
  }

  if (variant === 'ringing') {
    return (
      <div className={`relative ${className}`}>
        <div className={`${sizeClasses[size]} bg-gradient-to-br from-teal to-teal/80 rounded-xl flex items-center justify-center shadow-lg ${isRinging ? 'animate-wiggle' : ''}`}>
          <i className={`ri-phone-line ${iconSizes[size]} text-white`}></i>
        </div>
        <style>{`
          @keyframes wiggle {
            0%, 100% { transform: rotate(0deg); }
            25% { transform: rotate(-15deg); }
            75% { transform: rotate(15deg); }
          }
          .animate-wiggle {
            animation: wiggle 0.3s ease-in-out 3;
          }
        `}</style>
      </div>
    );
  }

  if (variant === 'pulse') {
    return (
      <div className={`relative ${className}`}>
        <div className="absolute inset-0 bg-teal/30 rounded-full animate-ping"></div>
        <div className={`relative ${sizeClasses[size]} bg-gradient-to-br from-teal to-teal/80 rounded-full flex items-center justify-center shadow-lg`}>
          <i className={`ri-phone-line ${iconSizes[size]} text-white`}></i>
        </div>
      </div>
    );
  }

  return null;
};

export default PhoneAnimation;
